import { useCallback, useEffect, useRef, useState } from 'react';

/** 工具条 / 滚动条轨道默认底色 */
const DEFAULT_BG_COLOR = '#EDF0F2';

/** 向下滚动超过该值后收起工具条（px） */
const TOOLBAR_HIDE_SCROLL_THRESHOLD = 15;

/** sessionStorage：按报告 path 记住当前会话内用户手动关闭工具条 */
const TOOLBAR_DISMISSED_STORAGE_PREFIX = 'dbh.aigallery-report.toolbar.dismissed';

const getToolbarDismissedKey = (path: string): string =>
  `${TOOLBAR_DISMISSED_STORAGE_PREFIX}:${path}`;

const readToolbarDismissed = (path: string): boolean => {
  if (!path) return false;
  try {
    return sessionStorage.getItem(getToolbarDismissedKey(path)) === '1';
  } catch {
    return false;
  }
};

/** 注入到 iframe 的滚动条样式节点 id */
const IFRAME_SCROLLBAR_STYLE_ID = 'dbh-aigallery-report-scrollbar';

/**
 * 读取 iframe body 背景色，透明则用默认色
 * @param doc - iframe 文档
 */
const readBodyBg = (doc: Document): string => {
  const bg = getComputedStyle(doc.body).backgroundColor;
  if (!bg || bg === 'transparent' || bg.startsWith('rgba(0, 0, 0, 0)')) {
    return DEFAULT_BG_COLOR;
  }
  return bg;
};

/**
 * 注入滚动条：滑块 #d8dae4，轨道跟 body 同色；hover 才出现，gutter 防抖
 * @param doc - iframe 文档
 * @param bg - 轨道 / 页面底色
 */
const injectScrollbarStyle = (doc: Document, bg: string): void => {
  const css = `
html, body {
  overflow-y: hidden !important;
  scrollbar-gutter: stable;
  background-color: ${bg} !important;
}
html:hover, body:hover { overflow-y: auto !important; }
::-webkit-scrollbar {
  width: 4px !important;
  height: 4px !important;
  background-color: ${bg} !important;
}
::-webkit-scrollbar-button { display: none !important; }
::-webkit-scrollbar-track,
::-webkit-scrollbar-corner {
  background-color: ${bg} !important;
}
::-webkit-scrollbar-thumb {
  background-color: #d8dae4 !important;
  border-radius: 8px !important;
}
`;
  let styleEl = doc.getElementById(IFRAME_SCROLLBAR_STYLE_ID);
  if (!styleEl) {
    styleEl = doc.createElement('style');
    styleEl.id = IFRAME_SCROLLBAR_STYLE_ID;
    (doc.head || doc.documentElement).appendChild(styleEl);
  }
  styleEl.textContent = css;
};

const getScrollTop = (target: EventTarget | null): number => {
  const el = target as Element | Document | null;
  if (!el) return 0;
  if (el.nodeType === 9) {
    const d = el as Document;
    return (
      d.scrollingElement?.scrollTop
      ?? d.documentElement.scrollTop
      ?? d.body?.scrollTop
      ?? 0
    );
  }
  return 'scrollTop' in el ? (el as Element).scrollTop : 0;
};

/**
 * 报告工具条展示：背景跟随 iframe body；isDismissed / scrollVisible 两个布尔值控制显隐
 * @param iframeRef - 报告内容 iframe
 * @param path - 报告 path，用于记住关闭状态
 */
export const useReportToolbarDisplay = (
  iframeRef: React.RefObject<HTMLIFrameElement>,
  path: string,
): {
    /** 工具条底色，与 iframe body / 滚动条轨道一致 */
    bgColor: string;
    /** 滚动收起后是否仍展示工具条 */
    scrollVisible: boolean;
    /** 用户是否已通过 X 关闭（当前 session 内刷新仍隐藏） */
    isDismissed: boolean;
    /** 关闭工具条并写入 sessionStorage */
    dismissToolbar: () => void;
  } => {
  const [bgColor, setBgColor] = useState<string>(DEFAULT_BG_COLOR);
  const [isDismissed, setIsDismissed] = useState<boolean>(() =>
    readToolbarDismissed(path),
  );
  const [scrollVisible, setScrollVisible] = useState<boolean>(true);
  const bgObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    setIsDismissed(readToolbarDismissed(path));
  }, [path]);

  const dismissToolbar = useCallback((): void => {
    if (!path) return;
    try {
      sessionStorage.setItem(getToolbarDismissedKey(path), '1');
    } catch {
      // 存储失败仍隐藏当前会话
    }
    setIsDismissed(true);
  }, [path]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let removeScroll: (() => void) | undefined;

    const bindIframe = (): void => {
      removeScroll?.();
      removeScroll = undefined;

      try {
        const win = iframe.contentWindow;
        const doc = win?.document;
        if (!win || !doc?.body) return;

        const applyBg = (): void => {
          const bg = readBodyBg(doc);
          injectScrollbarStyle(doc, bg);
          iframe.style.backgroundColor = bg;
          setBgColor(bg);
        };
        applyBg();

        bgObserverRef.current?.disconnect();
        bgObserverRef.current = new MutationObserver(applyBg);
        bgObserverRef.current.observe(doc.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });

        if (isDismissed) {
          return;
        }

        const onScroll = (e: Event): void => {
          setScrollVisible(
            getScrollTop(e.target) <= TOOLBAR_HIDE_SCROLL_THRESHOLD,
          );
        };
        setScrollVisible(
          getScrollTop(doc) <= TOOLBAR_HIDE_SCROLL_THRESHOLD,
        );
        win.addEventListener('scroll', onScroll, { capture: true, passive: true });
        removeScroll = () => {
          win.removeEventListener('scroll', onScroll, true);
        };
      } catch {
        // 跨域无法读取，保持默认底色、工具条常显
      }
    };

    iframe.addEventListener('load', bindIframe);
    if (iframe.contentDocument?.body) {
      bindIframe();
    }

    return () => {
      removeScroll?.();
      bgObserverRef.current?.disconnect();
      iframe.removeEventListener('load', bindIframe);
    };
  }, [iframeRef, isDismissed, path]);

  return { bgColor, scrollVisible, isDismissed, dismissToolbar };
};
